// Página OrderConfirmation - Confirmación de pedido
// Muestra la confirmación después de crear un pedido

import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../hooks/useCart'
import { getOrder } from '../services/orders.service'
import { createSale, getSaleByOrderId } from '../services/sales.service'
import { getWarehouse } from '../services/warehouses.service'
import { syncMercadoPagoOrderPayment } from '../services/mercadopago.service'
import { formatCurrency, formatDate } from '../utils/helpers'
import Button from '../components/Button'
import PaymentMethodReceipt from '../components/PaymentMethodReceipt'
import './OrderConfirmation.css'

/**
 * Página OrderConfirmation
 * Muestra la confirmación del pedido después de crearlo
 */
function OrderConfirmation() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { clearCart } = useCart()
  const traceOptions = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('trace') || params.get('debug')
    const enabled = raw === '1' || raw === 'true'
    const traceId = params.get('traceId') || params.get('trace_id')
    return { trace: enabled, traceId: traceId || null }
  }, [])
  
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cartCleared, setCartCleared] = useState(false)
  const [refreshingPayment, setRefreshingPayment] = useState(false)
  const [syncAttempted, setSyncAttempted] = useState(false)
  const [pickupWarehouse, setPickupWarehouse] = useState(null)
  const [saleEnsured, setSaleEnsured] = useState(false)

  // Cargar el pedido
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true)
        const orderData = await getOrder(orderId)
        
        if (!orderData) {
          setError('Pedido no encontrado')
          return
        }
        
        setOrder(orderData)
      } catch (err) {
        console.error('Error cargando pedido:', err)
        setError(err.message || 'Error al cargar el pedido')
      } finally {
        setLoading(false)
      }
    }

    if (orderId) {
      fetchOrder()
    } else {
      setError('ID de pedido no válido')
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    if (!order || cartCleared) return
    if (order.payment?.type === 'gateway_mercadopago') {
      const status = order.paymentGateway?.status || null
      const orderStatus = order.status || null
      if (status !== 'approved' && orderStatus !== 'confirmed' && orderStatus !== 'delivered') return
    }
    clearCart()
    setCartCleared(true)
  }, [order, clearCart, cartCleared])

  useEffect(() => {
    if (!order || !orderId) return
    if (saleEnsured) return
    if (order.payment?.type !== 'gateway_mercadopago') return
    const status = order.paymentGateway?.status || null
    const orderStatus = order.status || null
    if (status !== 'approved' && orderStatus !== 'confirmed' && orderStatus !== 'delivered') return

    let cancelled = false

    const run = async () => {
      try {
        const existingSale = await getSaleByOrderId(orderId)
        if (cancelled) return
        if (existingSale) {
          setSaleEnsured(true)
          return
        }
        const saleStatus = order.status === 'delivered' ? 'completed' : 'pending_delivery'
        await createSale({
          orderId,
          orderNumber: order.orderNumber,
          customer: order.customer,
          items: order.items,
          subtotal: order.subtotal,
          discount: 0,
          taxes: 0,
          shipping: order.shipping,
          total: order.total,
          paymentMethod: order.payment?.label || order.payment?.type || 'MercadoPago',
          payment: order.payment || null,
          paymentGateway: order.paymentGateway || null,
          paymentAdjustment: order.paymentAdjustment || null,
          fulfillment: order.fulfillment || null,
          paymentProofs: [],
          saleDate: new Date(),
          notes: order.notes || '',
          status: saleStatus,
          paid: true,
          createdBy: null,
          createdByName: 'Sistema'
        })
      } catch (e) {
        console.error('Error creando venta para MercadoPago:', e)
      } finally {
        if (!cancelled) {
          setSaleEnsured(true)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [order, orderId, saleEnsured])

  useEffect(() => {
    if (!orderId || !order) return
    if (syncAttempted) return
    if (order.payment?.type !== 'gateway_mercadopago') return
    const status = order.paymentGateway?.status || null
    if (status && status !== 'created' && status !== 'pending' && status !== 'in_process') return

    let cancelled = false

    const run = async () => {
      try {
        await syncMercadoPagoOrderPayment(orderId, traceOptions)
        if (cancelled) return
        const updated = await getOrder(orderId)
        if (updated) {
          setOrder(updated)
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Error sincronizando pago con MercadoPago:', e)
        }
      } finally {
        if (!cancelled) {
          setSyncAttempted(true)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [orderId, order, syncAttempted, traceOptions])

  useEffect(() => {
    if (!orderId || !order) return
    if (order.payment?.type !== 'gateway_mercadopago') return
    const status = order.paymentGateway?.status || null
    if (status && status !== 'pending' && status !== 'in_process') return

    let cancelled = false
    let attempts = 0
    setRefreshingPayment(true)

    const checkStatus = async () => {
      try {
        const updatedOrder = await getOrder(orderId)
        if (!updatedOrder || cancelled) return { done: true }
        const nextStatus = updatedOrder.paymentGateway?.status || null
        setOrder(updatedOrder)
        if (nextStatus && nextStatus !== 'pending' && nextStatus !== 'in_process') {
          if (!cancelled) {
            setRefreshingPayment(false)
          }
          return { done: true }
        }
        return { done: false }
      } catch (e) {
        if (!cancelled) {
          console.error('Error actualizando pedido:', e)
        }
        return { done: false }
      }
    }

    checkStatus()

    const intervalId = setInterval(async () => {
      attempts += 1
      if (attempts % 4 === 0) {
        try {
          await syncMercadoPagoOrderPayment(orderId, traceOptions)
        } catch (e) {
          if (!cancelled) {
            console.error('Error reintentando sincronización de MercadoPago:', e)
          }
        }
      }
      const result = await checkStatus()
      if (result.done || attempts >= 20) {
        if (!cancelled) {
          setRefreshingPayment(false)
        }
        clearInterval(intervalId)
      }
    }, 3000)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [orderId, order, traceOptions])

  const isMercadoPagoPayment = order?.payment?.type === 'gateway_mercadopago'
  const paymentGatewayStatus = order?.paymentGateway?.status || null
  const isPaymentApproved = isMercadoPagoPayment && paymentGatewayStatus === 'approved'
  const fulfillmentMethod = order?.fulfillment?.method === 'pickup'
    ? 'pickup'
    : order?.fulfillment?.method === 'negotiation'
      ? 'negotiation'
      : 'delivery'
  const pickupWarehouseId = typeof order?.fulfillment?.pickupWarehouseId === 'string' ? order.fulfillment.pickupWarehouseId : ''

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (fulfillmentMethod !== 'pickup' || !pickupWarehouseId) {
        setPickupWarehouse(null)
        return
      }
      try {
        const wh = await getWarehouse(pickupWarehouseId)
        if (!cancelled) setPickupWarehouse(wh || null)
      } catch (_e) {
        if (!cancelled) setPickupWarehouse(null)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [fulfillmentMethod, pickupWarehouseId])

  const getPaymentStatusLabel = (status) => {
    if (!status) return 'Esperando confirmación de pago'
    if (status === 'approved') return 'Pago aprobado'
    if (status === 'created') return 'Pago pendiente de confirmación'
    if (status === 'in_process') return 'Pago en proceso de validación'
    if (status === 'pending') return 'Pago pendiente de confirmación'
    if (status === 'rejected') return 'Pago rechazado'
    if (status === 'cancelled') return 'Pago cancelado'
    if (status === 'refunded') return 'Pago reembolsado'
    return 'Estado de pago desconocido'
  }

  const getPaymentStatusClassName = (status) => {
    if (status === 'approved') return 'status-delivered'
    if (status === 'rejected' || status === 'cancelled') return 'status-cancelled'
    return 'status-pending'
  }

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="loading-state">
            <p>Cargando confirmación...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="error-state">
            <h2>Error</h2>
            <p>{error || 'No se pudo cargar la información del pedido'}</p>
            <Button onClick={() => navigate('/')}>
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Formatear fecha
  const orderDate = order.createdAt?.toDate 
    ? formatDate(order.createdAt.toDate())
    : formatDate(new Date())

  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        {/* Icono de éxito */}
        <div className="confirmation-icon">
          <div className="success-circle">
            ✓
          </div>
        </div>

        {/* Título */}
        <h1 className="confirmation-title">
          {isMercadoPagoPayment && isPaymentApproved ? '¡Compra completada!' : '¡Pedido Confirmado!'}
        </h1>
        <p className="confirmation-subtitle">
          {isMercadoPagoPayment
            ? isPaymentApproved
              ? 'Tu pago fue acreditado correctamente. En breve procesaremos tu pedido.'
              : paymentGatewayStatus === 'rejected' || paymentGatewayStatus === 'cancelled'
                ? 'Tu pedido fue generado pero el pago no se completó. Podés completar el pago desde Mis Pedidos o elegir otro medio de pago.'
                : refreshingPayment
                  ? 'Estamos validando tu pago con MercadoPago. En unos minutos actualizaremos el estado y recibirás una notificación por email y/o WhatsApp cuando el pago se confirme.'
                  : 'Tu pedido ha sido recibido. Si el pago no se acredita, podrás completarlo luego desde Mis Pedidos.'
            : 'Tu pedido ha sido recibido correctamente'}
        </p>

        {/* Información del pedido */}
        <div className="confirmation-info">
          <div className="info-card main-info-card">
            <div className="info-row">
              <span className="info-label">Número de Pedido:</span>
              <span className="info-value order-number">{order.orderNumber}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Fecha:</span>
              <span className="info-value">{orderDate}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Total:</span>
              <span className="info-value total-amount">{formatCurrency(order.total)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Estado:</span>
              <span className={`info-value status-badge status-${order.status}`}>
                {order.status === 'pending' && 'Pendiente'}
                {order.status === 'confirmed' && 'Confirmado'}
                {order.status === 'processing' && 'En Proceso'}
                {order.status === 'shipped' && 'Enviado'}
                {order.status === 'delivered' && 'Entregado'}
                {order.status === 'cancelled' && 'Cancelado'}
              </span>
            </div>
            {isMercadoPagoPayment && (
              <div className="info-row">
                <span className="info-label">Estado del pago:</span>
                <span className={`info-value status-badge ${getPaymentStatusClassName(paymentGatewayStatus)}`}>
                  {refreshingPayment && (!paymentGatewayStatus || paymentGatewayStatus === 'created' || paymentGatewayStatus === 'pending' || paymentGatewayStatus === 'in_process')
                    ? 'Validando pago con MercadoPago...'
                    : getPaymentStatusLabel(paymentGatewayStatus)}
                </span>
              </div>
            )}
          </div>

          {/* Información del cliente */}
          <div className="info-card">
            <h3 className="info-card-title">Datos de Entrega</h3>
            <div className="info-details">
              <p><strong>{order.customer.name}</strong></p>
              <p>{order.customer.email}</p>
              <p>{order.customer.phone}</p>
              {fulfillmentMethod === 'pickup' ? (
                <p className="address">
                  Retiro por sucursal<br />
                  {pickupWarehouse
                    ? (
                      <>
                        {pickupWarehouse.name}<br />
                        {pickupWarehouse.address ? `${pickupWarehouse.address}` : ''}
                      </>
                    )
                    : (pickupWarehouseId ? 'Sucursal seleccionada' : 'Sucursal pendiente')}
                </p>
              ) : fulfillmentMethod === 'negotiation' ? (
                <p className="address">
                  Envío a acordar con el vendedor<br />
                  {order.customer?.address?.street ? (
                    <>
                      {order.customer.address.street}<br />
                      {order.customer.address.city}, {order.customer.address.postalCode}<br />
                      {order.customer.address.province}
                      {order.customer.address.country && !['ar', 'arg', 'argentina', 'república argentina', 'republica argentina'].includes(String(order.customer.address.country).trim().toLowerCase()) ? (
                        <>
                          <br />
                          {order.customer.address.country}
                        </>
                      ) : null}
                    </>
                  ) : (
                    'Dirección no informada'
                  )}
                </p>
              ) : (
                <p className="address">
                  Envío a domicilio<br />
                  {order.customer.address.street}<br />
                  {order.customer.address.city}, {order.customer.address.postalCode}<br />
                  {order.customer.address.province}
                  {order.customer.address.country && !['ar', 'arg', 'argentina', 'república argentina', 'republica argentina'].includes(String(order.customer.address.country).trim().toLowerCase()) ? (
                    <>
                      <br />
                      {order.customer.address.country}
                    </>
                  ) : null}
                </p>
              )}
            </div>
          </div>

          {/* Resumen de productos */}
          <div className="info-card summary-card">
            <h3 className="info-card-title">Resumen del Pedido</h3>
            <div className="order-items">
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="order-item-info">
                    <p className="order-item-name">{item.productName}</p>
                    {item.variant && (
                      <p className="order-item-variant">
                        {item.variant.type}: {item.variant.name}
                      </p>
                    )}
                    <p className="order-item-quantity">Cantidad: {item.quantity}</p>
                  </div>
                  <p className="order-item-price">
                    {formatCurrency(item.price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              <div className="total-row">
                <span>{fulfillmentMethod === 'pickup' ? 'Retiro:' : 'Envío:'}</span>
                <span>
                  {fulfillmentMethod === 'pickup'
                    ? 'Sin cargo'
                    : fulfillmentMethod === 'negotiation'
                      ? 'A acordar'
                      : order.shipping === 0
                        ? <span className="free-shipping">Gratis</span>
                        : formatCurrency(order.shipping)}
                </span>
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <PaymentMethodReceipt
                  payment={order.payment}
                  adjustmentAmount={order.paymentAdjustment?.amount || 0}
                  formatCurrency={formatCurrency}
                />
              </div>
              <div className="total-row total-final">
                <span>Total:</span>
                <span>{formatCurrency(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Notas si existen */}
          {order.notes && (
            <div className="info-card">
              <h3 className="info-card-title">Notas Adicionales</h3>
              <p className="order-notes">{order.notes}</p>
            </div>
          )}

          {/* Mensaje de email */}
          <div className="email-notice">
            <p>
              📧 Te hemos enviado un email con los detalles del pedido a{' '}
              <strong>{order.customer.email}</strong>
            </p>
          </div>
        </div>

        {/* Acciones */}
        <div className="confirmation-actions">
          {order.payment?.type === 'gateway_mercadopago' && order.paymentGateway?.initPoint ? (
            <a href={order.paymentGateway.initPoint} target="_blank" rel="noreferrer">
              <Button variant="primary">
                Pagar con MercadoPago
              </Button>
            </a>
          ) : null}
          <Link to="/">
            <Button variant="primary">
              🏠 Volver al Inicio
            </Button>
          </Link>
          {currentUser && (
            <Link to="/mis-pedidos">
              <Button variant="outline">
                📦 Ver Mis Pedidos
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderConfirmation

